import { Link } from 'react-router-dom';
import { Wrench, Calendar, BookOpen, Calculator } from 'lucide-react';

// Change this to swap which lead magnet is displayed on the landing page
const ACTIVE_MAGNET: 'support' | 'pm-guide' | 'playbook' | 'calculator' = 'support';

const MAGNETS = {
  support: {
    icon: Wrench,
    headline: 'Equipment down? Send us a photo.',
    subtext: 'Real expert, real answer. One free support ticket — no credit card, no strings.',
    cta: 'Get Your Free Diagnosis',
    href: '/free-support',
    color: 'blue',
  },
  'pm-guide': {
    icon: Calendar,
    headline: "The maintenance schedule your manufacturer should've given you.",
    subtext: 'A complete PM checklist for your equipment — the one that prevents the $400+ emergency call.',
    cta: 'Get Your Free PM Checklist',
    href: '/free-pm-guide',
    color: 'green',
  },
  playbook: {
    icon: BookOpen,
    headline: 'Stop sitting on hold with the manufacturer.',
    subtext: "Direct warranty lines, parts emails, service portals — the contacts they don't advertise. Pick a manufacturer, we'll send you the playbook.",
    cta: 'Get a Free Manufacturer Playbook',
    href: '/free-playbook',
    color: 'purple',
  },
  calculator: {
    icon: Calculator,
    headline: 'That repair quote sitting on your desk — is it worth it?',
    subtext: "Run the numbers on repair vs. replace. We'll email you the full analysis with our recommendation.",
    cta: 'Run the Free Calculator',
    href: '/free-calculator',
    color: 'blue',
  },
};

const colorMap: Record<string, { bg: string; border: string; text: string; pill: string; btnBg: string; btnHover: string; glow: string }> = {
  blue: {
    bg: 'bg-gradient-to-br from-blue-500/15 via-blue-500/10 to-blue-600/5',
    border: 'border-blue-400/40',
    text: 'text-blue-300',
    pill: 'bg-blue-500 text-white',
    btnBg: 'bg-blue-500 hover:bg-blue-400',
    btnHover: '',
    glow: 'shadow-lg shadow-blue-500/20',
  },
  green: {
    bg: 'bg-gradient-to-br from-green-500/15 via-green-500/10 to-green-600/5',
    border: 'border-green-400/40',
    text: 'text-green-300',
    pill: 'bg-green-500 text-white',
    btnBg: 'bg-green-500 hover:bg-green-400',
    btnHover: '',
    glow: 'shadow-lg shadow-green-500/20',
  },
  purple: {
    bg: 'bg-gradient-to-br from-purple-500/15 via-purple-500/10 to-purple-600/5',
    border: 'border-purple-400/40',
    text: 'text-purple-300',
    pill: 'bg-purple-500 text-white',
    btnBg: 'bg-purple-500 hover:bg-purple-400',
    btnHover: '',
    glow: 'shadow-lg shadow-purple-500/20',
  },
};

export default function LeadMagnetHero() {
  const magnet = MAGNETS[ACTIVE_MAGNET];
  const colors = colorMap[magnet.color] || colorMap.blue;
  const Icon = magnet.icon;

  return (
    <section className="pt-4 md:pt-6 pb-2 md:pb-3 px-4 md:px-6">
      <div className={`relative max-w-xl mx-auto text-center rounded-2xl border-2 ${colors.border} ${colors.bg} ${colors.glow} px-6 py-6 md:py-7`}>
        {/* FREE pill — solid for visual weight */}
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-extrabold tracking-[2px] uppercase ${colors.pill} shadow-lg`}>
          Free · No Account
        </div>
        <div className="flex items-center justify-center gap-2 mb-2.5 mt-1">
          <Icon className={`w-5 h-5 ${colors.text}`} />
          <h2 className="text-base md:text-lg font-bold text-white leading-tight">{magnet.headline}</h2>
        </div>
        <p className="text-white/75 text-xs md:text-sm mb-4 max-w-md mx-auto leading-relaxed">{magnet.subtext}</p>
        <Link
          to={magnet.href}
          className={`inline-block px-6 py-2.5 rounded-lg ${colors.btnBg} text-white text-sm font-bold transition-colors active:scale-[0.98] shadow-md`}
        >
          {magnet.cta} →
        </Link>
      </div>
    </section>
  );
}
